// The small dialog shown once, the moment a person arrives in a demo plan: who they are
// in it, and then the scenario's own `welcome` paragraphs (`@mor/eval`'s `scenario.ts`) —
// see `PlanScreen.tsx` for where this mounts, and AGENTS.md's "Text that ships is not code"
// for the rules that copy is checked against before being written.
//
// NOT A MESSAGE IN THE CONVERSATION. Mor reads every message in the room it is speaking
// in, so anything addressed to the person from inside it would be something Mor read
// too. This is interface around the conversation, the same as `.demoMark` and the
// character-sheet link, never a `facilitator` message the way a scenario's own opening
// line is (`docs/authored-openings.md`).
//
// Every paragraph is the scenario's own words — nothing here is generated. The FIRST one
// is shown as the dialog's heading; the rest are body prose. A scenario that wants the
// line naming who the visiting person is ("You're Daniel, the father.") writes it first,
// with `{{character}}` standing in for the seat's own standalone introduction
// (`seat.standaloneIntroduction`, from `useDemoSeatIntro`, falling back to `introduction`)
// — substituted below, since no scenario file knows which seat a visitor took. A
// paragraph whose placeholder cannot be resolved (neither written for this seat) is
// dropped entirely rather than shown with a raw token or an empty gap.
//
// Dismissed by the one button, and remembered per plan in `localStorage`
// (`demo/welcome-seen.ts`) — the same once-only convenience `release-notes-seen.ts`
// already keeps. A store that fails to remember still lets the button close the dialog
// for this view; it just will not stay closed next time, which is the failure this is
// allowed to have.

import { useEffect, useState } from 'react';

import type { DemoSeatIntro } from '../demo/useDemoSeatIntro.js';
import { demoWelcomeSeen, markDemoWelcomeSeen } from '../demo/welcome-seen.js';
import { welcomeParagraphs } from '../demo/welcome-text.js';

export type DemoWelcomeProps = {
  planId: string;
  seat: DemoSeatIntro;
};

/** The one substitution a `welcome` paragraph may carry — see `scenario.ts`'s own header
 *  on `SavedScenario.welcome` for why it is named rather than positional. */
const CHARACTER_PLACEHOLDER = '{{character}}';

export function DemoWelcome({ planId, seat }: DemoWelcomeProps): JSX.Element | null {
  const [open, setOpen] = useState(false);

  // Checked once per plan on arrival, not on every render — a dismissal earlier in this
  // same visit already set `open` to false directly, in `dismiss` below.
  useEffect(() => {
    setOpen(!demoWelcomeSeen(planId));
  }, [planId]);

  const dismiss = (): void => {
    markDemoWelcomeSeen(planId);
    setOpen(false);
  };

  // Escape dismisses, matching `DemoDeleteControl`'s own confirmation dialog.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        dismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div className="demoWelcome__scrim" onClick={dismiss}>
      <div
        className="demoWelcome__dialog"
        role="dialog"
        aria-label={`You’re ${seat.firstName}`}
        onClick={(event) => event.stopPropagation()}
      >
        {welcomeParagraphs(seat).map((paragraph, index) => (
          <p key={index} className={index === 0 ? 'demoWelcome__heading' : undefined}>
            {paragraph}
          </p>
        ))}
        <div className="demoWelcome__actions">
          <button type="button" className="button button--primary" onClick={dismiss}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
